import * as React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { introwise } from 'common/types';
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore';
import firebase from 'client/utils/firebase';
import errorHandler from 'client/utils/error-handler';

import { NotificationsContext } from 'client/components/NotificationsContext';

import { eventUrl, circleUrl, circleUrlCommunity, communityUrl } from 'client/utils/urls';
import { getEventPhotoUrl } from 'client/utils/statics';

import media from 'client/styles/media';

import LoadingIndicator from 'client/components/LoadingIndicator';

function hexToRgbA(hex: string, alpha: number) {
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    const cNum = Number(`0x${c.join('')}`);
    /* eslint no-bitwise: ["error", { "allow": ["&", ">>"] }] */
    /* eslint no-mixed-operators: 0 */
    return `rgba(${[(cNum >> 16) & 255, (cNum >> 8) & 255, cNum & 255].join(',')}, ${alpha})`;
  }
  throw new Error('Bad Hex');
}

const NotificationEventTitle = styled.div`
  height: 50px;
  line-height: 20px;
  font-weight: bolder;
  color: #ffffff;
  overflow: hidden;
  position: relative;
  padding: 5px;
`;

const NotificationEventTitleText = styled.div`
  position: relative;
  z-index: 1;
`;

const NotificationsEventTitle = ({ color, photo, title }: { color: string; photo: string; title: string }) => {
  const rgbaColor = color ? hexToRgbA(color, 0.7) : 'rgba(255, 255, 255, 0.7)';

  return (
    <NotificationEventTitle>
      <div
        style={{
          position: 'absolute',
          top: '-5px',
          left: '-5px',
          bottom: '-5px',
          right: '-5px',
          background: `linear-gradient(${rgbaColor}, ${rgbaColor}) no-repeat center/cover, url(${getEventPhotoUrl(
            photo,
          )}) no-repeat center/cover`,
          backgroundBlendMode: 'darken',
          filter: 'blur(5px)',
        }}
      />
      <NotificationEventTitleText>{title}</NotificationEventTitleText>
    </NotificationEventTitle>
  );
};

const notificationTextMixin = css`
  flex: 1 1 auto;
  line-height: 20px;
  font-size: 14px;
  padding: 5px;
`;

const NotificationTextLink = styled(Link)`
  display: block;
  ${notificationTextMixin}
`;

const NotificationTextBlock = styled.div`
  ${notificationTextMixin}
`;

const NotificationIcon = styled.div`
  flex: 0 0 25px;
  font-size: 18px;
  width: 25px;
  color: ${({ theme }) => theme.color.muted};
  text-align: center;
`;

const NotificationMessages = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationMessageCommunity | introwise.NotificationMessageEvent;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-envelope-square" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={
        notification.type === 'message-community'
          ? circleUrlCommunity({ circleId: notification.circleId, communitySlug: notification.communitySlug })
          : circleUrl({ eventId: notification.eventId, circleId: notification.circleId })
      }
    >
      <b>{notification.count}</b> new message
      {notification.count > 1 && 's'} in <i>{notification.circleTitle}</i>
    </NotificationTextLink>
  </>
);

const NotificationCircleCreated = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationCircleCreatedEvent;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-comment-o" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={circleUrl({ eventId: notification.eventId, circleId: notification.circleId })}
    >
      <b>{notification.createdByName}</b> created a circle <i>{notification.circleTitle}</i>
    </NotificationTextLink>
  </>
);

const NotificationNewEventParticipants = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationNewEventParticipant;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-users" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={eventUrl({ eventId: notification.eventId, relativeUrl: '/participants' })}
    >
      <b>{notification.count}</b> participant{notification.count > 1 && 's'} joined
    </NotificationTextLink>
  </>
);

const NotificationNewCommunityMember = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationNewCommunityMember;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-users" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={communityUrl({ communitySlug: notification.communitySlug, relativeUrl: '/members' })}
    >
      <b>{notification.count}</b> member{notification.count > 1 && 's'} joined
    </NotificationTextLink>
  </>
);

const NotificationNewCommunityJoinRequest = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationNewCommunityJoinRequest;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-user-plus" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={communityUrl({ communitySlug: notification.communitySlug, relativeUrl: '/members/requests' })}
    >
      <b>{notification.count}</b> new join request{notification.count > 1 && 's'}
    </NotificationTextLink>
  </>
);

const NotificationCommunityJoinRequestApproved = ({
  notification,
  markRead,
}: {
  notification: introwise.NotificationCommunityJoinRequestApproved;
  markRead: () => void;
}) => (
  <>
    <NotificationIcon>
      <i className="fa fa-user-plus" />
    </NotificationIcon>
    <NotificationTextLink
      className="non-text-link"
      onClick={() => markRead()}
      to={communityUrl({ communitySlug: notification.communitySlug })}
    >
      Join request has been approved
    </NotificationTextLink>
  </>
);

const NotificationAny = ({
  notification,
  markRead,
}: {
  notification: introwise.Notification;
  markRead: () => void;
}) => {
  switch (notification.type) {
    case 'message':
    case 'message-community':
      return (
        <NotificationMessages notification={notification as introwise.NotificationMessageEvent} markRead={markRead} />
      );
    case 'circle-recommended':
    case 'circle-recommended-community':
    case 'circle-created':
    case 'circle-created-community':
      return (
        <NotificationCircleCreated
          notification={notification as introwise.NotificationCircleCreatedEvent}
          markRead={markRead}
        />
      );
    case 'new-event-participant':
      return (
        <NotificationNewEventParticipants
          notification={notification as introwise.NotificationNewEventParticipant}
          markRead={markRead}
        />
      );
    case 'new-community-join-request':
      return (
        <NotificationNewCommunityJoinRequest
          notification={notification as introwise.NotificationNewCommunityJoinRequest}
          markRead={markRead}
        />
      );
    case 'new-community-member':
      return (
        <NotificationNewCommunityMember
          notification={notification as introwise.NotificationNewCommunityMember}
          markRead={markRead}
        />
      );
    case 'community-join-request-approved':
      return (
        <NotificationCommunityJoinRequestApproved
          notification={notification as introwise.NotificationCommunityJoinRequestApproved}
          markRead={markRead}
        />
      );
    default:
      return null;
  }
};

const NotificationMarkRead = styled.div`
  flex: 0 0 25px;
  width: 25px;
  margin-left: auto;
  text-align: center;

  i.fa::before {
    content: '\f10c';
    color: ${({ theme }) => theme.color.brandOrange};

    &:hover,
    &.hover {
      content: '\f111';
    }
  }
`;

const NotificationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  border-bottom: 1px solid #e7e7e7;

  padding-top: 10px;
  padding-bottom: 10px;

  ${media.md} {
    padding-top: 0px;
    padding-bottom: 0px;
  }
`;

const NotificationRowView = ({
  notification,
  markRead,
}: {
  notification: introwise.Notification;
  markRead: () => void;
}) => (
  <NotificationRow>
    <NotificationAny notification={notification} markRead={markRead} />
    <NotificationMarkRead>
      <i
        className="fa clickable"
        title="Mark as read"
        role="button"
        onClick={() => markRead()}
        onKeyPress={() => markRead()}
        tabIndex={0}
      />
    </NotificationMarkRead>
  </NotificationRow>
);

const NotificationsForEvent = ({
  eventId,
  notifications,
  markRead,
}: {
  eventId: string;
  notifications: introwise.Notification[];
  markRead: (id: string) => void;
}) => {
  const [data, loading, error] = useDocumentDataOnce<introwise.Event<firebase.firestore.Timestamp>>(
    firebase
      .firestore()
      .collection('events')
      .doc(eventId),
    {
      idField: 'id',
    },
  );

  React.useEffect(() => {
    if (error) {
      errorHandler.report(error);
    }
  }, [error]);

  if (error) {
    return <p>{`Error! ${error.message}`}</p>;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!data) {
    return <p>Event not found</p>;
  }
  const event = data;
  return (
    <div>
      <Link className="non-text-link" to={eventUrl({ eventId, eventSlug: event.slug })}>
        <NotificationsEventTitle title={event.title} color={event.color} photo={event.photo} />
      </Link>
      {notifications.map(n => (
        <NotificationRowView notification={n} key={n.id} markRead={() => markRead(n.id)} />
      ))}
    </div>
  );
};

const NotificationsByEvents = ({
  notificationsByEventIds,
  markRead,
}: {
  notificationsByEventIds: { [key: string]: introwise.Notification[] };
  markRead: (id: string) => void;
}) => (
  <>
    {Object.keys(notificationsByEventIds).map(eventId => (
      <NotificationsForEvent
        eventId={eventId}
        notifications={notificationsByEventIds[eventId]}
        key={eventId}
        markRead={markRead}
      />
    ))}
  </>
);

const NotificationCommunityTitle = styled.div`
  font-weight: bolder;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 5px;
`;

const NotificationsForCommunity = ({
  communityId,
  notifications,
  markRead,
}: {
  communityId: string;
  notifications: introwise.Notification[];
  markRead: (id: string) => void;
}) => {
  const [data, loading, error] = useDocumentDataOnce<introwise.Community<firebase.firestore.Timestamp>>(
    firebase
      .firestore()
      .collection('communities')
      .doc(communityId),
    {
      idField: 'id',
    },
  );

  React.useEffect(() => {
    if (error) {
      errorHandler.report(error);
    }
  }, [error]);

  if (error) {
    return <p>{`Error! ${error.message}`}</p>;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (data) {
    const community = data;
    return (
      <div>
        <Link className="non-text-link" to={communityUrl({ communitySlug: community.slug })}>
          <NotificationCommunityTitle>{community.name}</NotificationCommunityTitle>
        </Link>
        {notifications.map(n => (
          <NotificationRowView notification={n} key={n.id} markRead={() => markRead(n.id)} />
        ))}
      </div>
    );
  }

  return null;
};

const NotificationsByCommunities = ({
  notificationsByCommunityIds,
  markRead,
}: {
  notificationsByCommunityIds: { [key: string]: introwise.Notification[] };
  markRead: (id: string) => void;
}) => (
  <>
    {Object.keys(notificationsByCommunityIds).map(communityId => (
      <NotificationsForCommunity
        communityId={communityId}
        notifications={notificationsByCommunityIds[communityId]}
        key={communityId}
        markRead={markRead}
      />
    ))}
  </>
);

const NotificationWebPushSuggestion = ({ notification }: { notification: introwise.NotificationWebPushSuggestion }) => (
  <NotificationRow>
    <NotificationIcon>
      <i className="fa fa-feed" />
    </NotificationIcon>
    <NotificationTextBlock>
      Get push notifications on updates in your events when you{"'"}re not on Introwise. You can disable them anytime.
      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '5px 0px' }}>
        <button className="btn btn-sm btn-text" onClick={() => notification.dismiss()} type="button">
          Not now
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => notification.enable()} type="button">
          Enable
        </button>
      </div>
    </NotificationTextBlock>
  </NotificationRow>
);

const NotificationsOther = ({ notifications }: { notifications: introwise.Notification[] }) => {
  return (
    <>
      {notifications.map(n => {
        switch (n.type) {
          case 'webpush-suggestion':
            return (
              <NotificationWebPushSuggestion key={n.id} notification={n as introwise.NotificationWebPushSuggestion} />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

const NotificationsDrawerEmpty = styled.div`
  padding: 5px;
`;

const NotificationsDrawer = () => {
  const { notifications, loading, markRead } = React.useContext(NotificationsContext);
  if (loading) {
    return <LoadingIndicator />;
  }
  if (!notifications || !notifications.length) {
    return (
      <NotificationsDrawerEmpty>
        No new notifications <i className="fa fa-smile-o" />
      </NotificationsDrawerEmpty>
    );
  }
  const notificationsByEventIds: { [key: string]: introwise.Notification[] } = {};
  const notificationsByCommunityIds: { [key: string]: introwise.Notification[] } = {};
  const notificationsOther: introwise.Notification[] = [];
  notifications.forEach(n => {
    switch (n.type) {
      case 'circle-created':
      case 'circle-recommended':
      case 'message':
      case 'new-event-participant':
        notificationsByEventIds[n.eventId] = (notificationsByEventIds[n.eventId] || []).concat(n);
        break;
      case 'circle-created-community':
      case 'circle-recommended-community':
      case 'message-community':
      case 'new-community-join-request':
      case 'new-community-member':
      case 'community-join-request-approved':
        notificationsByCommunityIds[n.communityId] = (notificationsByCommunityIds[n.communityId] || []).concat(n);
        break;
      case 'webpush-suggestion':
        notificationsOther.push(n);
        break;
      default:
        break;
    }
  });

  const markReadById = (id: string) => markRead({ id });

  return (
    <>
      <NotificationsByCommunities notificationsByCommunityIds={notificationsByCommunityIds} markRead={markReadById} />
      <NotificationsByEvents notificationsByEventIds={notificationsByEventIds} markRead={markReadById} />
      <NotificationsOther notifications={notificationsOther} />
    </>
  );
};

export { NotificationTextBlock, NotificationsDrawerEmpty, NotificationMarkRead, NotificationRow };
export default NotificationsDrawer;
